import React, { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClient, IApiClient, MockApiClient } from '.';

interface RestClientContextType {
    client: IApiClient;
}

const RestClientContext = createContext<RestClientContextType | undefined>(undefined);

interface RestClientProviderProps {
    children: ReactNode;
    baseUrl: string;
    authCookieName?: string;
}

export const RestClientProvider: React.FC<RestClientProviderProps> = ({
    children,
    baseUrl,
    authCookieName = 'auth_token'
}) => {

    const navigate = useNavigate();

    const client = useMemo<IApiClient>(() => {
        const useMock = process.env.REACT_APP_USE_MOCK_CLIENT === 'true';
        if (useMock) return new MockApiClient();
        return new ApiClient(baseUrl, authCookieName);
    }, [baseUrl, authCookieName]);

    useEffect(() => {
        client.onUnauthorized = () => {
            navigate('/login');
        };
    }, [client, navigate]);

    const contextValue = useMemo(() => ({ client }), [client]);

    return (
        <RestClientContext.Provider value={contextValue}>
            {children}
        </RestClientContext.Provider>
    );
};

export const useRestClient = (): IApiClient => {
    const context = useContext(RestClientContext);
    if (context === undefined) {
        throw new Error('useRestClient must be used within a RestClientProvider');
    }
    return context.client;
}; 